import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    Typography,
    Grid,
    Link,
    Box,
    CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const passwordRules = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?#&^])[A-Za-z\d@$!%*?#&^]{8,}$/;

const VerificationForm = ({
    email,
    onConfirm,
    onResend,
    errorMessage,
    showEmailField = false,
    loading = false,
    success = false,
    requirePassword = false
}) => {
    const [code, setCode] = useState('');
    const [localEmail, setLocalEmail] = useState(email || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isCodeEntered, setIsCodeEntered] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        setIsCodeEntered(code.trim().length >= 6); // enable password fields when code is reasonably filled
    }, [code]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const usedEmail = showEmailField ? localEmail : email;

        if (requirePassword) {
            if (password !== confirmPassword) {
                setValidationError('Passwords do not match.');
                return;
            }

            if (!passwordRules.test(password)) {
                setValidationError(
                    'Password must contain at least 1 number, 1 special character, 1 uppercase, and 1 lowercase letter.'
                );
                return;
            }
        }

        setFormLoading(true);
        await onConfirm(code, usedEmail, requirePassword ? password : undefined);
        setFormLoading(false);
    };

    if (success) {
        return (
            <Box textAlign="center" mt={4}>
                <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />
                <Typography variant="h6" color="green" mt={2}>
                    Email confirmed successfully!
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    You can now <Link href="/">sign in</Link> with your credentials.
                </Typography>
            </Box>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            {showEmailField && (
                <TextField
                    fullWidth
                    margin="normal"
                    label="Email"
                    value={localEmail}
                    onChange={(e) => setLocalEmail(e.target.value)}
                    required
                />
            )}

            <TextField
                fullWidth
                margin="normal"
                label="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                disabled={
                    formLoading || loading || (showEmailField && localEmail.trim() === '')
                }
            />

            {requirePassword && (
                <>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="New Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={!isCodeEntered || formLoading || loading}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Confirm Password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={!isCodeEntered || formLoading || loading}
                    />
                </>
            )}

            {(errorMessage || validationError) && (
                <Typography variant="body2" color="error" align="center" sx={{ mt: 1 }}>
                    {errorMessage || validationError}
                </Typography>
            )}

            <Box display="flex" justifyContent="center" mt={2}>
                <Button
                    type="submit"
                    variant="contained"
                    sx={{ backgroundColor: '#0a0068', color: '#fff', width: '50%' }}
                    disabled={formLoading || loading}
                >
                    {formLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
                </Button>
            </Box>

            <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => onResend(showEmailField ? localEmail : email)}
                        disabled={formLoading || loading}
                    >
                        Resend verification code
                    </Link>
                </Grid>
            </Grid>

            <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                    <Link href="/" variant="body2">
                        Back to Sign In
                    </Link>
                </Grid>
            </Grid>
        </form>
    );
};

export default VerificationForm;
